// SQLite Database Service using sql.js (WebAssembly)
// Stores trading data locally in the browser

import initSqlJs, { Database as SqlJsDatabase } from "sql.js";

let db: SqlJsDatabase | null = null;

// Initialize database
export async function initDatabase(): Promise<void> {
  if (db) return;

  const SQL = await initSqlJs({
    locateFile: (file) => `https://sql.js.org/dist/${file}`,
  });

  // Try to load existing database from localStorage
  const savedData = localStorage.getItem("hodl_it_db");
  if (savedData) {
    const data = new Uint8Array(JSON.parse(savedData));
    db = new SQL.Database(data);
  } else {
    db = new SQL.Database();
    createTables();
  }
}

// Create tables
function createTables(): void {
  if (!db) return;

  // Trades table
  db.run(`
    CREATE TABLE IF NOT EXISTS trades (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      symbol TEXT NOT NULL,
      side TEXT NOT NULL,
      qty REAL NOT NULL,
      price REAL NOT NULL,
      total REAL NOT NULL,
      fee REAL DEFAULT 0,
      status TEXT DEFAULT 'pending',
      alpaca_order_id TEXT,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Signals table
  db.run(`
    CREATE TABLE IF NOT EXISTS signals (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      symbol TEXT NOT NULL,
      signal_type TEXT NOT NULL,
      signal_value TEXT,
      score INTEGER DEFAULT 0,
      reason TEXT,
      triggered BOOLEAN DEFAULT FALSE,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Portfolio snapshots
  db.run(`
    CREATE TABLE IF NOT EXISTS portfolio_snapshots (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      total_value REAL NOT NULL,
      cash REAL NOT NULL,
      positions_value REAL NOT NULL,
      pnl REAL DEFAULT 0,
      pnl_percent REAL DEFAULT 0,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Watchlist
  db.run(`
    CREATE TABLE IF NOT EXISTS watchlist (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      symbol TEXT NOT NULL UNIQUE,
      name TEXT,
      added_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Notes
  db.run(`
    CREATE TABLE IF NOT EXISTS notes (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      symbol TEXT,
      title TEXT NOT NULL,
      content TEXT,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
    )
  `);

  // Covered Calls table
  db.run(`
    CREATE TABLE IF NOT EXISTS covered_calls (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      symbol TEXT NOT NULL,
      shares REAL NOT NULL,
      purchase_price REAL NOT NULL,
      strike_price REAL NOT NULL,
      premium REAL NOT NULL,
      expiration_date TEXT NOT NULL,
      notes TEXT,
      status TEXT NOT NULL DEFAULT 'active',
      break_even_price REAL NOT NULL,
      max_profit REAL NOT NULL,
      initial_cost_basis REAL NOT NULL,
      realized_pl REAL,
      close_date TEXT,
      created_at TEXT NOT NULL,
      updated_at TEXT NOT NULL
    )
  `);

  saveDatabase();
}

// Save to localStorage
function saveDatabase(): void {
  if (!db) return;
  const data = db.export();
  const arr = Array.from(data);
  localStorage.setItem("hodl_it_db", JSON.stringify(arr));
}

// ── Trades ────────────────────────────────────────────────────────────────
export interface Trade {
  id?: number;
  symbol: string;
  side: "buy" | "sell";
  qty: number;
  price: number;
  total: number;
  fee?: number;
  status: string;
  alpaca_order_id?: string;
  created_at?: string;
}

export function addTrade(trade: Omit<Trade, "id" | "created_at">): number {
  if (!db) return -1;
  
  db.run(
    `INSERT INTO trades (symbol, side, qty, price, total, fee, status, alpaca_order_id) 
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [trade.symbol, trade.side, trade.qty, trade.price, trade.total, trade.fee || 0, trade.status, trade.alpaca_order_id || null]
  );
  
  saveDatabase();
  const result = db.exec("SELECT last_insert_rowid() as id");
  return result[0]?.values[0]?.[0] as number || -1;
}

export function getTrades(limit = 50): Trade[] {
  if (!db) return [];
  
  const result = db.exec(`SELECT * FROM trades ORDER BY created_at DESC LIMIT ${limit}`);
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const trade: Record<string, unknown> = {};
    columns.forEach((col, i) => { trade[col] = row[i]; });
    return trade as unknown as Trade;
  });
}

export function getTradesBySymbol(symbol: string): Trade[] {
  if (!db) return [];
  
  const result = db.exec(`SELECT * FROM trades WHERE symbol = ? ORDER BY created_at DESC`, [symbol]);
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const trade: Record<string, unknown> = {};
    columns.forEach((col, i) => { trade[col] = row[i]; });
    return trade as unknown as Trade;
  });
}

// ── Signals ────────────────────────────────────────────────────────────────
export interface Signal {
  id?: number;
  symbol: string;
  signal_type: string;
  signal_value?: string;
  score: number;
  reason?: string;
  triggered?: boolean;
  created_at?: string;
}

export function addSignal(signal: Omit<Signal, "id" | "created_at">): number {
  if (!db) return -1;
  
  db.run(
    `INSERT INTO signals (symbol, signal_type, signal_value, score, reason, triggered) 
     VALUES (?, ?, ?, ?, ?, ?)`,
    [signal.symbol, signal.signal_type, signal.signal_value || null, signal.score, signal.reason || null, signal.triggered ? 1 : 0]
  );
  
  saveDatabase();
  const result = db.exec("SELECT last_insert_rowid() as id");
  return result[0]?.values[0]?.[0] as number || -1;
}

export function getSignals(limit = 100): Signal[] {
  if (!db) return [];
  
  const result = db.exec(`SELECT * FROM signals ORDER BY created_at DESC LIMIT ${limit}`);
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const signal: Record<string, unknown> = {};
    columns.forEach((col, i) => { signal[col] = row[i]; });
    return signal as unknown as Signal;
  });
}

export function getRecentSignals(symbol?: string): Signal[] {
  if (!db) return [];
  
  const query = symbol 
    ? `SELECT * FROM signals WHERE symbol = ? ORDER BY created_at DESC LIMIT 20`
    : `SELECT * FROM signals ORDER BY created_at DESC LIMIT 20`;
    
  const result = symbol 
    ? db.exec(query, [symbol])
    : db.exec(query);
    
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const signal: Record<string, unknown> = {};
    columns.forEach((col, i) => { signal[col] = row[i]; });
    return signal as unknown as Signal;
  });
}

// ── Portfolio Snapshots ──────────────────────────────────────────────────
export interface PortfolioSnapshot {
  id?: number;
  total_value: number;
  cash: number;
  positions_value: number;
  pnl: number;
  pnl_percent: number;
  created_at?: string;
}

export function addPortfolioSnapshot(snapshot: Omit<PortfolioSnapshot, "id" | "created_at">): number {
  if (!db) return -1;
  
  db.run(
    `INSERT INTO portfolio_snapshots (total_value, cash, positions_value, pnl, pnl_percent) 
     VALUES (?, ?, ?, ?, ?)`,
    [snapshot.total_value, snapshot.cash, snapshot.positions_value, snapshot.pnl, snapshot.pnl_percent]
  );
  
  saveDatabase();
  const result = db.exec("SELECT last_insert_rowid() as id");
  return result[0]?.values[0]?.[0] as number || -1;
}

export function getPortfolioHistory(days = 30): PortfolioSnapshot[] {
  if (!db) return [];
  
  const result = db.exec(
    `SELECT * FROM portfolio_snapshots WHERE created_at >= datetime('now', '-${days} days') ORDER BY created_at ASC`
  );
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const snap: Record<string, unknown> = {};
    columns.forEach((col, i) => { snap[col] = row[i]; });
    return snap as unknown as PortfolioSnapshot;
  });
}

// ── Watchlist ─────────────────────────────────────────────────────────────
export interface WatchlistItem {
  id?: number;
  symbol: string;
  name?: string;
  added_at?: string;
}

export function addToWatchlist(symbol: string, name?: string): number {
  if (!db) return -1;
  
  db.run(`INSERT OR IGNORE INTO watchlist (symbol, name) VALUES (?, ?)`, [symbol, name || null]);
  saveDatabase();
  
  const result = db.exec("SELECT last_insert_rowid() as id");
  return result[0]?.values[0]?.[0] as number || -1;
}

export function removeFromWatchlist(symbol: string): void {
  if (!db) return;
  db.run(`DELETE FROM watchlist WHERE symbol = ?`, [symbol]);
  saveDatabase();
}

export function getWatchlist(): WatchlistItem[] {
  if (!db) return [];
  
  const result = db.exec(`SELECT * FROM watchlist ORDER BY added_at DESC`);
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const item: Record<string, unknown> = {};
    columns.forEach((col, i) => { item[col] = row[i]; });
    return item as unknown as WatchlistItem;
  });
}

// ── Notes ────────────────────────────────────────────────────────────────
export interface Note {
  id?: number;
  symbol?: string;
  title: string;
  content?: string;
  created_at?: string;
  updated_at?: string;
}

export function addNote(note: Omit<Note, "id" | "created_at" | "updated_at">): number {
  if (!db) return -1;
  
  db.run(
    `INSERT INTO notes (symbol, title, content) VALUES (?, ?, ?)`,
    [note.symbol || null, note.title, note.content || null]
  );
  saveDatabase();
  
  const result = db.exec("SELECT last_insert_rowid() as id");
  return result[0]?.values[0]?.[0] as number || -1;
}

export function updateNote(id: number, content: string): void {
  if (!db) return;
  db.run(`UPDATE notes SET content = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?`, [content, id]);
  saveDatabase();
}

export function getNotes(symbol?: string): Note[] {
  if (!db) return [];
  
  const query = symbol
    ? `SELECT * FROM notes WHERE symbol = ? OR symbol IS NULL ORDER BY updated_at DESC`
    : `SELECT * FROM notes ORDER BY updated_at DESC`;
    
  const result = symbol
    ? db.exec(query, [symbol])
    : db.exec(query);
    
  if (!result[0]) return [];
  
  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const note: Record<string, unknown> = {};
    columns.forEach((col, i) => { note[col] = row[i]; });
    return note as unknown as Note;
  });
}

export function deleteNote(id: number): void {
  if (!db) return;
  db.run(`DELETE FROM notes WHERE id = ?`, [id]);
  saveDatabase();
}

// ── Covered Calls ─────────────────────────────────────────────────────────
export interface CoveredCallRecord {
  id?: number;
  symbol: string;
  shares: number;
  purchase_price: number;
  strike_price: number;
  premium: number;
  expiration_date: string;
  notes?: string;
  status: string;
  break_even_price: number;
  max_profit: number;
  initial_cost_basis: number;
  realized_pl?: number;
  close_date?: string;
  created_at: string;
  updated_at: string;
}

export function addCoveredCall(position: Omit<CoveredCallRecord, "id" | "created_at" | "updated_at">): number {
  if (!db) return -1;

  const now = new Date().toISOString();
  db.run(
    `INSERT INTO covered_calls (symbol, shares, purchase_price, strike_price, premium, expiration_date, notes, status, break_even_price, max_profit, initial_cost_basis, realized_pl, close_date, created_at, updated_at) 
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      position.symbol,
      position.shares,
      position.purchase_price,
      position.strike_price,
      position.premium,
      position.expiration_date,
      position.notes || null,
      position.status,
      position.break_even_price,
      position.max_profit,
      position.initial_cost_basis,
      position.realized_pl || null,
      position.close_date || null,
      now,
      now,
    ]
  );

  saveDatabase();
  const result = db.exec("SELECT last_insert_rowid() as id");
  return result[0]?.values[0]?.[0] as number || -1;
}

export function getCoveredCalls(): CoveredCallRecord[] {
  if (!db) return [];

  const result = db.exec(`SELECT * FROM covered_calls ORDER BY expiration_date ASC`);
  if (!result[0]) return [];

  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const record: Record<string, unknown> = {};
    columns.forEach((col, i) => { record[col] = row[i]; });
    return record as unknown as CoveredCallRecord;
  });
}

export function getCoveredCallById(id: number): CoveredCallRecord | null {
  if (!db) return null;

  const result = db.exec(`SELECT * FROM covered_calls WHERE id = ?`, [id]);
  if (!result[0] || !result[0].values[0]) return null;

  const columns = result[0].columns;
  const row = result[0].values[0];
  const record: Record<string, unknown> = {};
  columns.forEach((col, i) => { record[col] = row[i]; });
  return record as unknown as CoveredCallRecord;
}

export function updateCoveredCall(id: number, updates: Partial<CoveredCallRecord>): void {
  if (!db) return;

  const fields: string[] = [];
  const values: unknown[] = [];

  Object.entries(updates).forEach(([key, value]) => {
    if (key !== 'id') {
      fields.push(`${key} = ?`);
      values.push(value as string | number | null);
    }
  });

  if (fields.length === 0) return;

  fields.push('updated_at = ?');
  values.push(new Date().toISOString());
  values.push(id);

  db.run(`UPDATE covered_calls SET ${fields.join(', ')} WHERE id = ?`, values as (string | number | null)[]);
  saveDatabase();
}

export function deleteCoveredCall(id: number): void {
  if (!db) return;
  db.run(`DELETE FROM covered_calls WHERE id = ?`, [id]);
  saveDatabase();
}

export function getActiveCoveredCalls(): CoveredCallRecord[] {
  if (!db) return [];

  const result = db.exec(`SELECT * FROM covered_calls WHERE status = 'active' ORDER BY expiration_date ASC`);
  if (!result[0]) return [];

  const columns = result[0].columns;
  return result[0].values.map((row) => {
    const record: Record<string, unknown> = {};
    columns.forEach((col, i) => { record[col] = row[i]; });
    return record as unknown as CoveredCallRecord;
  });
}

// ── Stats ────────────────────────────────────────────────────────────────
export function getStats() {
  if (!db) return null;
  
  const tradesResult = db.exec(`SELECT COUNT(*) as count, SUM(total) as volume FROM trades`);
  const signalsResult = db.exec(`SELECT COUNT(*) as count FROM signals WHERE triggered = 1`);
  const watchlistResult = db.exec(`SELECT COUNT(*) as count FROM watchlist`);
  
  return {
    totalTrades: tradesResult[0]?.values[0]?.[0] || 0,
    totalVolume: tradesResult[0]?.values[0]?.[1] || 0,
    totalSignals: signalsResult[0]?.values[0]?.[0] || 0,
    watchlistCount: watchlistResult[0]?.values[0]?.[0] || 0,
  };
}

// Export database (backup)
export function exportDatabase(): string {
  if (!db) return "";
  const data = db.export();
  const arr = Array.from(data);
  return btoa(String.fromCharCode(...arr));
}

// Import database (restore)
export function importDatabase(base64Data: string): void {
  const data = new Uint8Array(atob(base64Data).split("").map(c => c.charCodeAt(0)));
  initSqlJs({ locateFile: (file) => `https://sql.js.org/dist/${file}` }).then(async (SQL) => {
    db = new SQL.Database(data);
  });
}